import * as moment from 'moment';

import * as enums from './Enums';

import { CompanyModel } from './CompanyModel';
import { CustomerModel, CustomerWithoutRegisterModel } from './CustomerModel';
import { ProductImageModel } from './ProductModel';
import { VendorModel, VendorStoreModel } from './VendorModel';
import { ShippingProviderModel } from './ShippingModel';
import { EventModel } from './EventModel';
import { PointTemplateModel } from './PointModel';

import { envVariables } from "@shared/mock-data";
import { Constants } from "@shared/shared.constants";
import { CustomerCardModel } from './CardModel';
import { CommunityModel } from './CommunityModel';
import { PackageModel } from './PackageModel';
import { AssignedType, CurrencyType, IdCardCustomerType, OrderStatus, PayType, SaleStatus } from './Enums';
import { PackageScratchCustomerModel } from './PackageScratchCustomerModel';
import { CurrencyInfo } from '../currency-info';


export enum TransactionActivityType {
    None = 0,
    Purchase = 1,
    Paid = 2,
    Sent = 3,
    RequestToPrepare = 4,
    Preparing = 5,
    ReadyToPickup = 6,
    Expired = 7
}

export class TransactionActivityModel {

    public constructor(init?: Partial<Pick<TransactionActivityModel, keyof TransactionActivityModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            Object.assign(this, init);
        }
    }

    id = 0;
    transactionId = 0;
    customerId = 0;

    activityType = TransactionActivityType.None;
    total = 0;
    points = 0;
    createdAt: Date;

    customer?: CustomerModel;

    chat: TransactionChatModel[] = [];

    get typeIcon(): string {

        switch (this.activityType) {
            case TransactionActivityType.Purchase:
                return 'mdi mdi-arrow-down';
            case TransactionActivityType.Paid:
                return 'mdi mdi-star';
            case TransactionActivityType.Sent:
                return 'mdi mdi-arrow-right';
            case TransactionActivityType.RequestToPrepare:
                return 'assets/images/cookingIcon.png';
            case TransactionActivityType.ReadyToPickup:
                return 'mdi mdi-arrow-left';
        }
    }
}

export class TransactionModel {

    public constructor(init?: Partial<Pick<TransactionModel, keyof TransactionModel>>) {

        if (init) {
            if (init.company) {
                init.company = new CompanyModel(init.company);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.customerOwner) {
                init.customerOwner = new CustomerModel(init.customerOwner);
            }
            if (init.customerCard) {
                init.customerCard = new CustomerCardModel(init.customerCard);
            }
            if (init.vendor) {
                init.vendor = new VendorModel(init.vendor);
            }
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (typeof init.expirationAt === 'string') {
                init.expirationAt = new Date(init.expirationAt);
            }
            if (typeof init.lastReceivedAt === 'string') {
                init.lastReceivedAt = new Date(init.lastReceivedAt);
            }
            if (typeof init.redeemedAt == 'string') {
                init.redeemedAt = new Date(init.redeemedAt);
            }
            if (typeof init.qrDownloadedAt == 'string') {
                init.qrDownloadedAt = new Date(init.qrDownloadedAt);
            }
            if (init.activities?.length) {
                init.activities = init.activities.map((item: any) => new TransactionActivityModel(item))
                    .sort((a: TransactionActivityModel, b: TransactionActivityModel) => b.createdAt.getTime() - a.createdAt.getTime());
            }
            if (init.coupons?.length) {
                init.coupons = init.coupons.map((item: any) => new TransactionCouponModel(item));
            }
            if (init.couponsWallet?.length) {
                init.couponsWallet = init.couponsWallet.map((item: any) => new CouponWalletModel(item));
            }
            if (init.giftcards?.length) {
                init.giftcards = init.giftcards.map((item: any) => new TransactionGiftcardModel(item));
            }
            if (init.products?.length) {
                init.products = init.products.map((item: any) => new TransactionProductModel(item));
            }
            if (init.tickets?.length) {
                init.tickets = init.tickets.map((item: any) => new TransactionTicketModel(item));
            }
            if (init.transactionNotes) {
                init.transactionNotes = new TransactionNotesModel(init.transactionNotes);
            }
            if (init.greeting) {
                init.greeting = new TransactionGreetingModel(init.greeting);
            }
            if (init.transactionPackage) {
                init.transactionPackage = new TransactionPackageModel(init.transactionPackage);
            }
            Object.assign(this, init);
        }
    }

    id = 0;
    type: enums.PointTransactionType;

    buyerType: BuyerType;

    companyId: number;
    company?: CompanyModel;

    customerId: number;
    customer?: CustomerModel;

    customerOwnerId: number;
    customerOwner?: CustomerModel;

    customerSenderId: number;
    customerSender?: CustomerModel;

    customerCard: CustomerCardModel;

    vendorId: number;
    vendor?: VendorModel;

    storeId: number;
    store?: VendorStoreModel;

    vendorUserId: number;
    vendorCustomerId: number;

    orderNumber: string;

    country = enums.Country.CR;

    transactionNotes?: TransactionNotesModel;

    greeting?: TransactionGreetingModel;

    paymentAt: Date;
    expirationAt: Date;

    purchaseType = enums.PurchaseType.GiviApp;

    paymentMethodType = enums.PaymentMethodType.VisaAndMasterCard;

    orderStatus = enums.OrderStatus.Pending;
    assignedType = enums.AssignedType.None;
    lastReceivedAt: Date;

    redeemedAt: Date;
    redeemedType = enums.RedeemedType.GiviApp;

    isLite = false;
    //Indica si el Givi se puede descargar como Codigo QR")]
    allowDownload = false;
    qrDownloadedAt?: Date;

    subTotal = 0;
    SubTotalWithDiscount = 0;
    taxTotal = 0; // No tiene IVA 
    commissionTotal = 0;
    total = 0;

    points = 0;

    comments: string;

    idCardType: enums.IdCardType;
    idCard: string;

    shippingProviderId?: number;
    saleStatus: enums.SaleStatus;

    communityId: number = 0;
    community: CommunityModel;

    province = 0;
    canton = 0;
    district = 0;

    //lat: number;
    //lng: number;
    addressDetails: string;
    addressMap: string;

    cardName: string;
    cardMasked: string;
    cardToken: string;

    authorizationId: string;
    reasonText: string;
    systemTrace: string;
    retrievalRefNo: string;

    appleWalletUrl: string;
    googleWalletUrl: string;


    payType: enums.PayType;

    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;

    transactionPackage?: TransactionPackageModel;

    appProducts: TransactionProductModel[] = [];

    coupons: TransactionCouponModel[] = [];

    couponsWallet: CouponWalletModel[] = [];

    giftcards: TransactionGiftcardModel[] = [];

    products: TransactionProductModel[] = [];

    tickets: TransactionTicketModel[] = [];

    activities: TransactionActivityModel[] = [];


    // Get 
    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.vendor?.currencyType ?? CurrencyType.CRC);
    }


    set ticket(ticket: TransactionTicketModel) {
    }

    get ticket(): TransactionTicketModel {

        if (this.tickets?.length) {
            return this.tickets[0];
        }
        return null;
    }

    get isTicket(): boolean {

        return this.tickets?.length > 0;
    }

    get isDonation(): boolean {

        return this.giftcards?.length > 0 && this.giftcards[0].type == enums.GiftcardType.Donation;
    }

    get isGiftcard(): boolean {

        return this.giftcards?.length > 0 && this.giftcards[0].type == enums.GiftcardType.POP;
    }

    get isMerch(): boolean {

        return this.giftcards?.length > 0 && this.giftcards[0].type == enums.GiftcardType.Merch;
    }


    get isCoupon(): boolean {

        return this.coupons?.length > 0;
    }

    get isPack(): boolean {

        return this.transactionPackage != null;
    }

    get isProduct(): boolean {

        return this.products?.length > 0;
    }

    get isEvent(): boolean {

        if (this.ticket?.eventObj?.type == enums.EventType.EventWithExpirationDate
            || this.ticket?.eventObj?.type == enums.EventType.EventWithOutExpirationDate) {

            return true;
        }

        return false

    }

    get isCashless(): boolean {

        if (this.ticket?.eventObj?.type == enums.EventType.CashlessWithExpirationDate
            || this.ticket?.eventObj?.type == enums.EventType.CashlessWithOutExpirationDate) {

            return true;
        }

        return false

    }

    get isPasses(): boolean {

        if (this.ticket?.eventObj?.type == enums.EventType.PassWithExpirationDate
            || this.ticket?.eventObj?.type == enums.EventType.PassWithOutExpirationDate) {

            return true;
        }

        return false

    }


    // Get
    set coupon(product: TransactionCouponModel) {
    }

    get coupon(): TransactionCouponModel {

        if (this.coupons?.length) {
            return this.coupons[0];
        }
        return null;
    }

    // Get
    set giftcard(product: TransactionGiftcardModel) {
    }

    get giftcard(): TransactionGiftcardModel {

        if (this.giftcards?.length) {
            return this.giftcards[0];
        }
        return null;
    }

    set product(product: TransactionProductModel) {
    }

    get product(): TransactionProductModel {

        if (this.products?.length) {
            return this.products[0];
        }
        return null;
    }


    get productName(): string {

        if (this.coupon) {
            return this.coupon.productName;
        }
        if (this.giftcard) {
            return this.giftcard.productName;
        }
        if (this.product) {
            return this.product.productName;
        }
        if (this.ticket) {
            return this.ticket.productName;
        }
        return '';
    }

    get productDescription(): string {

        if (this.coupon) {
            return this.coupon.description;
        }
        if (this.giftcard) {
            return this.giftcard.description;
        }
        if (this.product) {
            return this.product.description;
        }
        if (this.ticket) {
            return this.ticket.description;
        }
        return '';
    }

    get productURL(): string {

        if (this.product) {
            if (this.product.images && this.product.images.length) {
                return this.product.images[0].url;
            }
            return this.product.productImageAssociate;
        }
        if (this.giftcard && this.giftcard.images && this.giftcard.images.length) {
            return this.giftcard.images[0].url;
        }
        if (this.coupon) {
            return this.coupon.url;
        }
        if (this.ticket) {
            return this.ticket.eventObj.thumbnailUrl;
        }
        return 'assets/images/noimage.png';
    }

    get productQuantitySelected(): number {

        if (this.coupon) {
            return this.coupon.quantitySelected;
        }
        if (this.giftcard) {
            return this.giftcard.quantitySelected;
        }
        if (this.product) {
            return this.product.quantitySelected;
        }
        if (this.ticket) {
            return this.ticket.quantitySelected;
        }
        return 0;
    }

    get productStores(): VendorStoreModel[] {

        if (this.coupon) {
            return this.coupon.stores;
        }
        if (this.giftcard) {
            return this.giftcard.stores;
        }
        if (this.product) {
            return this.product.stores;
        }
        if (this.ticket) {
            return this.ticket.stores;
        }
        return [];
    }


    get isOnlyForGiviExpress(): boolean {

        /* Se comprueba si la propiedad `productShippingProviderId` y los `productStores` tienen un
        Id mayor que 0. Significa que todas las tiendas son Givi Express */
        if (this.productShippingProviderId > 0) {
            return this.productStores.every(x => x.shippingProviderId > 0);
        }
        return false;
    }

    get productShippingProviderId(): number {

        if (this.coupon) {
            return this.coupon.shippingProviderId;
        }
        if (this.giftcard) {
            return this.giftcard.shippingProviderId;
        }
        if (this.product) {
            return this.product.shippingProviderId;
        }
        return null;
    }
    get productShippingProvider(): ShippingProviderModel {

        if (this.coupon) {
            return this.coupon.shippingProvider;
        }
        if (this.giftcard) {
            return this.giftcard.shippingProvider;
        }
        if (this.product) {
            return this.product.shippingProvider;
        }
        return null;
    }

    get orderNumberText(): string {

        if (this.orderNumber) {
            const orderNumber = (this.orderNumber).padStart(6, '0');
            return `#${orderNumber}`;
        }
        return '';
    }

    get vendorName(): string {

        if (this.vendor) {
            return this.vendor.name;
        }
        return '';
    }


    set buyerId(id: number) {

        if (this.buyerType == BuyerType.Company) {
            this.companyId = id;
        }
        this.customerId = id;
    }

    get buyerId(): number {

        if (this.buyerType == BuyerType.Company) {
            return this.companyId;
        }
        return this.customerId;
    }

    // Mock Data
    get cardLogoURL(): string {

        if (this.cardName) {

            const brand = this.cardName.split('-')[0];

            if (brand.indexOf('MC') >= 0) {
                return 'assets/images/card-master.png';
            }
            if (brand.indexOf('AE') >= 0) {
                return 'assets/images/card-amex.png';
            }
            if (brand.indexOf('VI') >= 0) {
                return 'assets/images/card-visa.png';
            }
        }

    }

    get logoURL(): string {

        if (this.buyerType == BuyerType.Company) {
            return this.company.logoURL;
        }
        if (this.customer) {
            return this.customer.logoURL;
        }
        if (this.customerOwner) {
            return this.customerOwner.logoURL;
        }
    }

    get name(): string {

        if (this.buyerType == BuyerType.Company) {
            return this.company.businessName;
        }
        if (this.customer) {
            return this.customer.fullname;
        }
        if (this.customerOwner) {
            return this.customerOwner.fullname;
        }
    }

    get phone(): string {

        if (this.buyerType == BuyerType.Company) {
            return this.company.phone;
        }
        if (this.customer) {
            return this.customer.phone;
        }
        if (this.customerOwner) {
            return this.customerOwner.phone;
        }
    }

    get email(): string {

        if (this.buyerType == BuyerType.Company) {
            return this.company.email;
        }
        if (this.customer) {
            return this.customer.email;
        }
        if (this.customerOwner) {
            return this.customerOwner.email;
        }
    }

    get emailShort(): string {

        if (this.email.length > 30) {

            let result = '';
            let arrayEmail = this.email.split('@');
            result = arrayEmail[0].substring(0, 11) + '***' + '@' + arrayEmail[1];
            return result;
        }
        else {
            return this.email;
        }
    }

    get qrCode(): string {

        if (this.id) {

            /* Rellena el id con ceros a la izquierda para garantizar que tenga al menos 6 caracteres. */
            const code = (this.id.toString()).padStart(6, '0');
            return code;
        }
    }

    get qrCodeLite(): string {

        if (this.id) {

            if (this.isLite || this.allowDownload) {

                /* Rellena el id con ceros a la izquierda para garantizar que tenga al menos 6 caracteres. */
                const code = `${this.id.toString().padStart(6, '0')}-LT-${this.productId.toString().padStart(4, '0')}`;
                return code;
            }
            return this.qrCode;
        }
    }

    get isExpired(): boolean {

        if (this.isCashless) return false;

        const todayDate = new Date().toISOString().slice(0, 10);
        const expiration = new Date(this.expirationAt);
        const expirationString = expiration.toISOString().slice(0, 10);

        const valid = moment(todayDate).isAfter(moment(expirationString));

        return valid;
    }

    get mainImage(): string {

        if (this.coupon) {
            return this.coupon.productImageAssociate;
        }
        if (this.giftcard) {
            return this.giftcard.images[0]?.url;
        }
        if (this.product) {
            return this.product.images[0]?.url;
        }
        if (this.ticket) {
            return this.ticket.images[0]?.url;
        }
        return '';
    }

    get productId(): number {

        if (this.coupon) {
            return this.coupon.productId;
        }
        if (this.giftcard) {
            return this.giftcard.productId;
        }
        if (this.product) {
            return this.product.productId;
        }
        if (this.ticket) {
            return this.ticket.productId;
        }
        return 0;
    }

    get productSku(): string {

        if (this.coupon) {
            return this.coupon.sku;
        }
        if (this.giftcard) {
            return this.giftcard.sku;
        }
        if (this.product) {
            return this.product.sku;
        }
        if (this.ticket) {
            return this.ticket.sku;
        }
        return '';
    }
    get productSkuLength(): number {

        if (this.productSku) {
            // Regular expression to identify HTML tags in the input string. 
            // Replacing the identified HTML tag with a null string.
            const text = this.productSku.replace(/(<([^>]+)>)/ig, '');

            return text.length;
        }
        return 0;
    }

    // Used to display in the Mis Givis
    isForGift = false;

     // Used to display in the Mis Givis
    isForOwner = false;

    get validPackage(): boolean {

        let result = true;

        // Valida que si la transferencia tiene asociado un paquete y no esta activa no muestre los beneficios
        if (this.transactionPackage && this.transactionPackage.activatedAt == null) {
            result = false;
        }

        return result;
    }
}


export class TransactionChatResponseModel {

    public constructor(init?: Partial<Pick<TransactionChatResponseModel, keyof TransactionChatResponseModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            Object.assign(this, init);
        }
    }

    id = 0;
    chatId = 0;
    vendorResponseType: VendorResponseType;
    customerResponseType: CustomerResponseType;
    createdAt: Date;

    get customerResponse(): string {

        switch (this.customerResponseType) {
            case CustomerResponseType.Yes:
                return 'Si';
            case CustomerResponseType.No:
                return 'No';
            case CustomerResponseType.Prepare:
                return 'Preparar';
            case CustomerResponseType.Giftcard:
                return 'Givi POPs';
            case CustomerResponseType.StoreToPickup:
                return 'Store name';
        }
    }
}

export class TransactionChatModel {

    public constructor(init?: Partial<Pick<TransactionChatModel, keyof TransactionChatModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            Object.assign(this, init);
        }
    }

    id = 0;

    transactionId = 0;

    createdAt: Date;

    actionType: ChatActionType;

    responses: TransactionChatResponseModel[] = [];
}


export class CouponWalletModel {

    public constructor(init?: Partial<Pick<CouponWalletModel, keyof CouponWalletModel>>) {

        if (init) {
            if (typeof init.expirationAt === 'string') {
                init.expirationAt = new Date(init.expirationAt);
            }
            Object.assign(this, init);
        }
    }

    transactionId: number;
    productId: number;
    name: string;
    appleWalletUrl: string;
    googleWalletUrl: string;
    expirationAt?: Date;
}

export class TransactionCouponModel {

    public constructor(init?: Partial<Pick<TransactionCouponModel, keyof TransactionCouponModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (init.vendor) {
                init.vendor = new VendorModel(init.vendor);
            }
            if (init.stores && init.stores.length) {
                init.stores = init.stores.map((item: any) => new VendorStoreModel(item));
            }
            this.quantitySelected = init.quantity || 1;
            Object.assign(this, init);
        }
    }

    id: number;
    transactionId: number;

    vendorId?: number;

    shippingProviderId?: number;
    shippingProvider: ShippingProviderModel;

    sku: string;
    name = '';
    description: string;
    terms: string;

    applicationType = enums.CouponApplicationType.General;

    vendorDiscountType: enums.VendorDiscountType;
    vendorPrice = 0; // Sin IVA
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15

    price = 0;
    points = 0;

    productId: number;

    isAvailable = true;

    taxType = enums.ProductTaxType.Exempt;
    taxPercent = 0;

    quantity = 1;

    isActive = true;
    isVisible = true;

    createdAt = new Date;
    updatedAt?: Date;


    productImageAssociate: string;
    productGiftCardImageAssociate: string;

    vendor: VendorModel;


    stores: VendorStoreModel[] = [];


    quantitySelected = 1;

    isMultiVendor: boolean = false;


    set productName(v: string) {
        this.name = v;
    }
    get productName(): string {
        return this.name;
    }

    set url(v: string) {
        this.productImageAssociate = v;
    }
    get url(): string {
        return this.productImageAssociate;
    }

    // Used to display in the form invoice
    get priceTotal(): number {

        return this.price * this.quantitySelected;
    }

    // Used to display in the form invoice
    get pointsTotal(): number {

        return this.points * this.quantitySelected;
    }
}

export class TransactionGiftcardModel {

    public constructor(init?: Partial<Pick<TransactionGiftcardModel, keyof TransactionGiftcardModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (init.vendor) {
                init.vendor = new VendorModel(init.vendor);
            }
            if (init.stores && init.stores.length) {
                init.stores = init.stores.map((item: any) => new VendorStoreModel(item));
            }
            if (init.images && init.images.length) {
                init.images = init.images.map((item: any) => new ProductImageModel(item));
            }
            if (init.vendors && init.vendors.length) {
                init.vendors = init.vendors.map((item: any) => new VendorModel(item));
            }
            this.quantitySelected = init.quantity || 1;
            Object.assign(this, init);
        }
    }

    id: number;
    transactionId: number;

    vendorId?: number;

    shippingProviderId?: number;
    shippingProvider: ShippingProviderModel;

    productId: number;

    sku: string;
    name = '';
    description: string;

    allowDiscount: boolean;
    discountPercent: number;

    vendorDiscountType: enums.VendorDiscountType;
    vendorPrice = 0; // Sin IVA
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15

    price = 0;
    points = 0;

    type: enums.GiftcardType;

    // Product is variant
    isVariant = false;
    // Inventory is generic
    isGeneric = true;
    isAvailable = true;

    taxType = enums.ProductTaxType.Exempt;
    taxPercent = 0;

    quantity = 1;
    weight = 0.1;

    isActive = true;
    isVisible = true;

    createdAt = new Date;
    updatedAt?: Date;


    vendor: VendorModel;

    images: ProductImageModel[] = [];

    stores: VendorStoreModel[] = [];

    vendors: VendorModel[] = [];


    quantitySelected = 1;


    set productName(v: string) {
        this.name = v;
    }
    get productName(): string {
        return this.name;
    }

    set url(v: string) {
    }
    get url(): string {
        return (this.images && this.images.length) ? this.images[0].url : null;
    }


    get priceWithDiscount(): number {

        if (this.allowDiscount && this.discountPercent >= 0 && this.discountPercent <= 100) {

            const price = this.price - ((this.discountPercent / 100) * this.price);
            return price;
        } else {

            return this.price;
        }
    }

    // Used to display in the form invoice
    get priceTotal(): number {

        return this.price * this.quantitySelected;
    }

    // Used to display in the form invoice
    get pointsTotal(): number {

        return this.points * this.quantitySelected;
    }
}

export class TransactionProductModel {

    public constructor(init?: Partial<Pick<TransactionProductModel, keyof TransactionProductModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (init.vendor) {
                init.vendor = new VendorModel(init.vendor);
            }
            if (init.stores && init.stores.length) {
                init.stores = init.stores.map((item: any) => new VendorStoreModel(item));
            }
            if (init.images && init.images.length) {
                init.images = init.images.map((item: any) => new ProductImageModel(item));
            }
            this.quantitySelected = init.quantity || 1;
            Object.assign(this, init);
        }
    }

    id: number;
    transactionId: number;

    vendorId?: number;
    storeId?: number;

    shippingProviderId?: number;
    shippingProvider: ShippingProviderModel;

    productId: number;

    sku: string;
    name = '';
    description: string;

    allowDiscount: boolean;
    discountPercent: number;

    vendorDiscountType: enums.VendorDiscountType;
    vendorPrice = 0; // Sin IVA
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15

    price = 0;
    points = 0;


    // Product is variant
    isVariant = false;
    // Inventory is generic
    isGeneric = true;
    isAvailable = true;

    taxType = enums.ProductTaxType.Exempt;
    taxPercent = 0;

    quantity = 1;
    weight = 0.1;

    isActive = true;
    isVisible = true;

    createdAt = new Date;
    updatedAt?: Date;


    vendor: VendorModel;

    images: ProductImageModel[] = [];
    productImageAssociate: string;

    stores: VendorStoreModel[] = [];



    quantitySelected = 1;


    set productName(v: string) {
        this.name = v;
    }
    get productName(): string {
        return this.name;
    }

    set url(v: string) {
    }
    get url(): string {
        return (this.images && this.images.length) ? this.images[0].url : null;
    }


    get priceWithDiscount(): number {

        if (this.allowDiscount && this.discountPercent >= 0 && this.discountPercent <= 100) {

            const price = this.price - ((this.discountPercent / 100) * this.price);
            return price;
        } else {

            return this.price;
        }
    }

    // Used to display in the form invoice
    get priceTotal(): number {

        return this.price * this.quantitySelected;
    }

    // Used to display in the form invoice
    get pointsTotal(): number {

        return this.points * this.quantitySelected;
    }
}

export class TransactionTicketModel {

    public constructor(init?: Partial<Pick<TransactionTicketModel, keyof TransactionTicketModel>>) {

        if (init) {
            if (init.eventObj) {
                init.eventObj = new EventModel(init.eventObj);
            }
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.saleDateStartAt === 'string') {
                init.saleDateStartAt = new Date(init.saleDateStartAt);
            }
            this.quantitySelected = init.quantity || 1;

            Object.assign(this, init);
        }
    }

    id: number;
    transactionId: number;

    storeId: number;
    productId: number;
    vendorId?: number;

    sku: string;
    name = '';
    terms = '';
    description: string;
    instructions: string;

    vendorDiscountType: enums.VendorDiscountType;
    vendorPrice = 0; // Sin IVA
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15

    price = 0;
    points = 0;


    // Inventory is generic
    isGeneric = true;
    isAvailable = true;

    taxType = enums.ProductTaxType.Exempt;
    taxPercent = 0;
    tax = 0;
    discount: number;

    quantity = 1;

    currencyType = enums.CurrencyType.CRC;

    isActive = true;
    isVisible = true;

    createdAt = new Date;
    updatedAt?: Date;


    productImageAssociate: string;
    thumbnailUrl: string;


    vendor: VendorModel;
    images: ProductImageModel[] = [];
    stores: VendorStoreModel[] = [];
    eventObj: EventModel;

    shippingProviderId: number;

    quantitySelected = 1;

    // Se setea en el servidor
    quantityOfPlaces = 1;

    saleDateStartAt: Date;

    // productImageAssociate convertido a base64
    imageToBase64: string;


    set productName(v: string) {
        this.name = v;
    }
    get productName(): string {
        return this.name;
    }

    set url(v: string) {
        this.productImageAssociate = v;
    }
    get url(): string {
        return this.productImageAssociate;
    }

    // Used to display in the form invoice
    get priceTotal(): number {

        return this.price * this.quantitySelected;
    }

    // Used to display in the form invoice
    get pointsTotal(): number {

        return this.points * this.quantitySelected;
    }
}


// Transferencia de un Givi, a la cuenta del usuario, puede ser por Link o Sms
export class TransactionTransferModel {

    public constructor(init?: Partial<Pick<TransactionTransferModel, keyof TransactionTransferModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    id = 0;
    type: enums.PointTransactionType;
    redeemedType: enums.RedeemedType.GiviApp;
    assignedType: enums.AssignedType = enums.AssignedType.None;

    points: number;

    customerId: number;
    customerEmail: string;
    customer: CustomerModel;

    couponId: number;
    communityId: number;
    uniqueLinkCode: string = null;
    scratchId: number;
    loyaltyPlanCustomerStickerId: number;
}



export class NewTransactionPointsModel {

    public constructor(init?: Partial<Pick<NewTransactionPointsModel, keyof NewTransactionPointsModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.products && init.products.length) {
                init.products = init.products.map((item: any) => new ProductOrderModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;

    buyerType = BuyerType.Customer;
    customerId: number;
    customer: CustomerModel;

    orderNumber: string;

    purchaseType = enums.RedeemedType.GiviApp;
    redeemedType = enums.RedeemedType.GiviApp;
    assignedType = enums.AssignedType.None;

    createdAt: Date;
    paymentAt: Date;

    orderStatus = enums.OrderStatus.Pending;

    //discountId: number;
    //discountPercent: number;
    discountAmount = 0;

    subTotal = 0;
    taxTotal = 0; // No tiene IVA 
    shippingTotal = 0;
    total = 0;

    points = 0;


    idCard: string;
    idCardType: enums.IdCardType;
    province = 0;
    canton = 0;
    district = 0;


    currencyType = enums.CurrencyType.CRC;

    cardToken: string;

    payType: enums.PayType;


    products: ProductOrderModel[] = [];

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.currencyType ?? CurrencyType.CRC);
    }
}



export interface IPurchaseWithPoints {

    customerId: number;
    customer: CustomerModel;

    id: number;

    subTotal: number;
    total: number;

    points: number;
    pointsCash: number;
}

export class NewTransactionGiftcardModel implements IPurchaseWithPoints {

    private _pointValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    public constructor(init?: Partial<Pick<NewTransactionGiftcardModel, keyof NewTransactionGiftcardModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.products && init.products.length) {
                init.products = init.products.map((item: any) => new ProductOrderModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;

    vendorId: number;

    buyerType: BuyerType;
    customerId: number;
    customer: CustomerModel;

    orderNumber: string;

    purchaseType = enums.PurchaseType.GiviApp;

    createdAt: Date;
    paymentAt: Date;

    orderStatus = enums.OrderStatus.Pending;

    // Commission
    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    subTotal = 0;
    taxTotal = 0; // No tiene IVA 
    total = 0;

    points = 0;
    pointsDiscount = 0;
    pointsValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    // Factura electronica
    idCard: string;
    idCardType: enums.IdCardType;
    province = 0;
    canton = 0;
    district = 0;

    currencyType = enums.CurrencyType.CRC;

    payType: enums.PayType;
    cardToken: string;
    allowSavingCard: boolean = true;

    communityId: number;

    products: ProductOrderModel[] = [];

    customerWithoutRegister: CustomerWithoutRegisterModel;

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.currencyType ?? CurrencyType.CRC);
    }


    public get pointsCash(): number {

        if (this.points > 0) {
            return this.points * this._pointValue;
        }
        return 0;
    }

    public get customerCommission(): number {

        return (this.total) * (this.customerCommissionPercent / 100);
    }
}

export class NewTransactionPackageModel implements IPurchaseWithPoints {

    private _pointValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    public constructor(init?: Partial<Pick<NewTransactionPackageModel, keyof NewTransactionPackageModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.products && init.products.length) {
                init.products = init.products.map((item: any) => new ProductOrderModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;

    vendorId: number;

    buyerType: BuyerType;
    customerId: number;
    customer: CustomerModel;

    orderNumber: string;

    purchaseType = enums.PurchaseType.GiviApp;

    createdAt: Date;
    paymentAt: Date;

    orderStatus = enums.OrderStatus.Pending;

    // Commission
    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    subTotal = 0;
    taxTotal = 0; // No tiene IVA 
    total = 0;

    points = 0;
    pointsDiscount = 0;
    pointsValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    // Factura electronica
    idCard: string;
    idCardType: enums.IdCardType;
    province = 0;
    canton = 0;
    district = 0;

    currencyType = enums.CurrencyType.CRC;

    payType: enums.PayType;
    cardToken: string;

    communityId: number;

    products: ProductOrderModel[] = [];

    customerWithoutRegister: CustomerWithoutRegisterModel;

    uniqueLinkCode: string;

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.currencyType ?? CurrencyType.CRC);
    } 

    public get pointsCash(): number {

        if (this.points > 0) {
            return this.points * this._pointValue;
        }
        return 0;
    }

    public get customerCommission(): number {

        return (this.total) * (this.customerCommissionPercent / 100);
    }
}
export class TransactionPackageModel {

    public constructor(init?: Partial<Pick<TransactionPackageModel, keyof TransactionPackageModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.customerOwner) {
                init.customerOwner = new CustomerModel(init.customerOwner);
            }
            if (init.transactionNotes) {
                init.transactionNotes = new TransactionNotesModel(init.transactionNotes);
            }
            if (init.transactions) {
                init.transactions = init.transactions.map((item: any) => new TransactionModel(item));;
            }
            if (init.createdAt == undefined && init.transactions.length > 0) {
                init.createdAt = init.transactions[0]?.createdAt;
            }
            Object.assign(this, init);
        }
    }

    id: number;
    packageId: number;
    vendorId?: number;

    buyerType: BuyerType;
    customerId?: number;
    customerOwnerId?: number;
    communityId?: number;
    community: CommunityModel;

    orderNumber: string;
    orderStatus: OrderStatus;
    saleStatus: SaleStatus;
    paymentAt?: Date;
    activatedAt?: Date;
    expirationAt?: Date;
    lastReceivedAt?: Date;
    createdAt?: Date;

    assignedType: AssignedType;
    discountAmount: number;
    discountPercent: number;
    subTotal: number;
    subTotalWithDiscount: number;
    taxTotal: number;
    commissionTotal: number;
    total: number;
    points: number;
    comments: string;
    idCard: string;
    idCardType: IdCardCustomerType;
    province: number;
    canton: number;
    district: number;
    lat?: number;
    lng?: number;
    addressDetails: string;
    addressMap: string;
    payType: PayType;
    currencyType: CurrencyType;
    tipoCambio: number;
    cardName: string;
    cardMasked: string;
    cardToken: string;
    authorizationId: string;
    reasonText: string;
    systemTrace: string;
    retrievalRefNo: string;

    invoiceDate?: Date;
    invoiceRef: string;
    invoiceTraceText: string;
    bankRefNum: string;
    transferProofImage: string;
    transferValidationAt?: Date;
    // transferValidationResponse: TransferValidationResponse;

    transferValidationNotes: string;

    customerName: string;
    customerEmail: string;

    deletedAt?: Date;

    customerCard: CustomerCardModel;
    customer?: CustomerModel;
    customerOwner?: CustomerModel;

    package?: PackageModel;
    vendor?: VendorModel;

    country = enums.Country.CR;

    transactions: TransactionModel[] = [];

    transactionNotes?: TransactionNotesModel;

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.vendor?.currencyType ?? CurrencyType.CRC);
    }
}

export class NewTransactionProductModel implements IPurchaseWithPoints {

    private _pointValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    public constructor(init?: Partial<Pick<NewTransactionProductModel, keyof NewTransactionProductModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.products && init.products.length) {
                init.products = init.products.map((item: any) => new ProductOrderModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;
    orderNumber: string;

    vendorId: number;
    communityId?: number;

    buyerType = BuyerType.Customer;
    customerId: number;
    customer: CustomerModel;

    purchaseType = enums.PurchaseType.GiviApp;

    createdAt: Date;
    paymentAt: Date;

    orderStatus = enums.OrderStatus.Pending;

    // Commission
    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    subTotal = 0;
    taxTotal = 0; // No tiene IVA 
    total = 0;

    points = 0;
    pointsDiscount = 0;
    pointsValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    // Factura electronica
    idCard: string;
    idCardType: enums.IdCardType;
    province = 0;
    canton = 0;
    district = 0;

    currencyType = enums.CurrencyType.CRC;

    payType: enums.PayType;
    cardToken: string;
    allowSavingCard: string;

    products: ProductOrderModel[] = [];

    customerWithoutRegister: CustomerWithoutRegisterModel;

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.currencyType ?? CurrencyType.CRC);
    }


    public get pointsCash(): number {

        if (this.points > 0) {
            return this.points * this._pointValue;
        }
        return 0;
    }
}

export class NewTransactionTicketModel implements IPurchaseWithPoints {

    private _pointValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    public constructor(init?: Partial<Pick<NewTransactionTicketModel, keyof NewTransactionTicketModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.products && init.products.length) {
                init.products = init.products.map((item: any) => new ProductOrderModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;

    vendorId: number;
    eventId: number;

    buyerType: BuyerType;
    customerId: number;
    customer: CustomerModel;

    purchaseType = enums.PurchaseType.GiviApp;

    isLite = false;
    allowDownload = false;

    createdAt: Date;
    paymentAt: Date;

    orderStatus = enums.OrderStatus.Pending;

    // Indica si el comercio asume los costos de comisión y validación
    isCommissionToVendor = false;

    // Commission
    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    subTotal = 0;
    taxTotal = 0; // No tiene IVA 

    // Comisión 10% (sin impuestos)
    commissionCost = 0;
    // Costo validación ($.50 all transactions)
    validationCost = 0;

    // all customer commission
    allCustomerCommission: number = 0;

    total = 0;

    communityId: number;

    points = 0;
    pointsDiscount = 0;
    pointsValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    // Factura electronica
    idCard: string;
    idCardType: enums.IdCardType;
    province = 0;
    canton = 0;
    district = 0;

    currencyType = enums.CurrencyType.CRC;

    payType = enums.PayType.Card;
    cardToken: string;
    allowSavingCard: string;

    products: ProductOrderModel[] = [];

    uniqueLinkCode: string = null;


    // Se utiliza para enviar los datos del customer cuando la compra es por el Checkout Express,
    customerWithoutRegister: CustomerWithoutRegisterModel;

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.currencyType ?? CurrencyType.CRC);
    }

    public get pointsCash(): number {

        if (this.points > 0) {
            return this.points * this._pointValue;
        }
        return 0;
    }
}

export class NewTransactionCouponModel implements IPurchaseWithPoints {

    private _pointValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    public constructor(init?: Partial<Pick<NewTransactionCouponModel, keyof NewTransactionCouponModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.paymentAt === 'string') {
                init.paymentAt = new Date(init.paymentAt);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.products && init.products.length) {
                init.products = init.products.map((item: any) => new ProductOrderModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;

    vendorId: number;

    buyerType: BuyerType;
    customerId: number;
    customer: CustomerModel;

    orderNumber: string;

    purchaseType = enums.PurchaseType.GiviApp;

    createdAt: Date;
    paymentAt: Date;

    orderStatus = enums.OrderStatus.Pending;

    // Commission
    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    subTotal = 0;
    taxTotal = 0; // No tiene IVA 
    total = 0;

    points = 0;
    pointsDiscount = 0;
    pointsValue = envVariables[Constants.ENV_POINTS_VALUE_KEY] ?? 1;

    // Factura electronica
    idCard: string;
    idCardType: enums.IdCardType;
    province = 0;
    canton = 0;
    district = 0;

    currencyType = enums.CurrencyType.CRC;

    payType: enums.PayType;
    cardToken: string;
    allowSavingCard: string;

    communityId: number;
    
    uniqueLinkCode: string = null;

    products: ProductOrderModel[] = [];

    customerWithoutRegister: CustomerWithoutRegisterModel;

    appleWalletUrl: string;
    googleWalletUrl: string;

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.currencyType ?? CurrencyType.CRC);
    } 

    public get pointsCash(): number {

        if (this.points > 0) {
            return this.points * this._pointValue;
        }
        return 0;
    }

    // Costo fijo del servicio en la moneda del comercio, seteado por el componente con
    // el valor del backend (GiviServiceCost). Ver nota en CouponModel.serviceCost.
    serviceCost = 0;

    public get customerCommission(): number {

        return ((this.total) * (this.customerCommissionPercent / 100)) + this.serviceCost;
    }
}

export class NewTransactionMarketCouponPointsModel {

    public constructor(init?: Partial<Pick<NewTransactionMarketCouponPointsModel, keyof NewTransactionMarketCouponPointsModel>>) {

        if (init) {
            if (init.template) {
                init.template = new PointTemplateModel(init.template);
            }
            Object.assign(this, init);
        }
    }

    customerId: number;

    points: number;


    from: string;
    to: string;
    message: string;

    attachmentType: enums.AttachmentType;
    attachmentUrl: string;


    template: PointTemplateModel;
}


export class ProductOrderModel {

    public constructor(init?: Partial<Pick<ProductOrderModel, keyof ProductOrderModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    id: number;
    productId: number;
    productVariantId: number;

    sku: string;
    productName: string;

    vendorDiscountType: enums.VendorDiscountType;
    vendorPrice = 0; // Sin IVA
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15
    vendorPriceWithTax = 0;

    // Commission
    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    points: number;

    quantity = 0;
    price = 0;
    tax = 0;
    discount: number;

    taxType = enums.ProductTaxType.Exempt;
    taxPercent = 0;

    currencyType = enums.CurrencyType.CRC;

    productImageId: number;

    productImageAssociate: string;


    shippingProviderId?: number;


    get totalPrice(): number {

        return this.price * this.quantity;
    }
}

export class TicketOrderModel extends ProductOrderModel {

    public constructor(init?: Partial<Pick<TicketOrderModel, keyof TicketOrderModel>>) {
        super(init)
        if (init) {
            Object.assign(this, init);
        }
    }

    quantityOfPlaces = 1;
}


export class TransactionPaymentModel {

    public constructor(init?: Partial<Pick<TransactionPaymentModel, keyof TransactionPaymentModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    id: number;
    token: string;
}

export class TransactionNotesModel {

    public constructor(init?: Partial<Pick<TransactionNotesModel, keyof TransactionNotesModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    id: number;
    transactionId: number;
    customerOwnerId: number;
    color: string;
    note: string;
    createdAt: Date;
    update: Date;
}

export class TransactionGreetingModel {

    public constructor(init?: Partial<Pick<TransactionGreetingModel, keyof TransactionGreetingModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    id: number;
    transactionId: number;
    customerId: number;

    from: string;
    to: string;
    message: string;

    attachmentType: enums.AttachmentType.None
    attachmentUrl: string;

    createdAt: Date;
    update: Date;
}

export class TransactionAccumulateModel {

    public constructor(init?: Partial<Pick<TransactionAccumulateModel, keyof TransactionAccumulateModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    transactionId: number;
    vendorUserId: number;
    vendorCustomerId: number;

    shippingProviderId: number;
    isSuccessful: boolean;
    messageResult: string;
}

export class TransactionPointsFilterModel {

    public constructor(init?: Partial<Pick<TransactionPointsFilterModel, keyof TransactionPointsFilterModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    startDate: Date;
    endDate: Date;
    customerId: number;

}



export class MyGivisModel {

    public constructor(init?: Partial<Pick<MyGivisModel, keyof MyGivisModel>>) {

        if (init) {
            if (init.transactions?.length) {
                init.transactions = init.transactions.map((item: any) => new TransactionModel(item));
            }
            if (init.packageScratch?.length) {
                init.packageScratch = init.packageScratch.map((item: any) => new PackageScratchCustomerModel(item));
            }
            Object.assign(this, init);
        }
    }

    transactions: TransactionModel[] = [];
    packageScratch: PackageScratchCustomerModel[] = [];
}


export enum BuyerType {
    All = 0,
    Customer = 1,
    Company = 2
}

export enum ChatActionType {
    RequestToPrepare = 1,
    Prepare = 2,
    ReadyToPickup = 3
}


export enum CustomerResponseType {
    No = 0,
    Yes = 1,
    Prepare = 2,
    Giftcard = 3,
    StoreToPickup = 4
}

export enum VendorResponseType {
    ProductSelect = 1,
    StoreSelect = 2,
    ProductPrepare = 3,
    ConfirmPickup = 4,
    ReadToPickup = 5
}